// =============================================================================
// Plugin Terminal Page - Appthen 插件终端页面
// =============================================================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebglAddon } from '@xterm/addon-webgl';
import { ClipboardAddon } from '@xterm/addon-clipboard';
import io from 'socket.io-client';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api.js';
import 'xterm/css/xterm.css';
import '../styles/plugin.css';

const PluginTerminalPage = () => {
  const { terminalId } = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const terminalRef = useRef(null);
  const terminalInstance = useRef(null);
  const socketRef = useRef(null);
  const fitAddon = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  // 获取认证 token - 优先从 URL 参数获取，然后从 localStorage
  const getAuthToken = useCallback(() => {
    // 1. 首先尝试从 URL query 参数获取
    const tokenFromQuery = searchParams.get('token');
    if (tokenFromQuery) {
      console.log('🔑 Using token from URL query parameter');
      return tokenFromQuery;
    }

    // 2. 然后尝试从 localStorage 获取
    const tokenFromStorage = localStorage.getItem('auth_token');
    if (tokenFromStorage) {
      console.log('🔑 Using token from localStorage');
      return tokenFromStorage;
    }

    console.warn('🔑 No authentication token found in URL or localStorage');
    return null;
  }, [searchParams]);

  // PostMessage API - 与 Appthen 通信
  const sendMessageToParent = useCallback((type, data) => {
    const message = {
      type,
      data,
      source: 'ai-workspace-terminal',
      terminalId,
      timestamp: new Date().toISOString()
    };

    console.log('🔌 Terminal sending message to parent:', message);

    if (window.parent && window.parent !== window) {
      window.parent.postMessage(message, '*');
    }
  }, [terminalId]);

  // 处理来自 Appthen 的消息
  const handleParentMessage = useCallback((event) => {
    // 安全检查：只处理来自预期源的消息
    if (!event.data || typeof event.data !== 'object') return;
    if (!event.data.type || event.data.source === 'ai-workspace-terminal') return;

    console.log('🔌 Terminal received message from parent:', event.data);

    const { type, data } = event.data;

    switch (type) {
      case 'TERMINAL_READY_CHECK':
        // Appthen 检查终端是否准备就绪
        sendMessageToParent('TERMINAL_READY', {
          ready: isConnected && !!terminalInstance.current,
          terminalId,
          connected: isConnected,
          error: error
        });
        break;

      case 'TERMINAL_COMMAND':
        // Appthen 发送命令到终端
        if (data?.command && terminalInstance.current && socketRef.current?.connected) {
          const command = data.command.endsWith('\n') ? data.command : data.command + '\n';
          socketRef.current.emit('terminal:input', {
            terminalId,
            input: command
          });

          sendMessageToParent('COMMAND_SENT', {
            command: data.command,
            success: true
          });
        } else {
          sendMessageToParent('COMMAND_SENT', {
            command: data?.command,
            success: false,
            error: 'Terminal not ready or not connected'
          });
        }
        break;

      case 'TERMINAL_RESIZE':
        // Appthen 请求调整终端大小
        if (data?.cols && data?.rows && fitAddon.current && terminalInstance.current) {
          try {
            terminalInstance.current.resize(data.cols, data.rows);
            fitAddon.current.fit();

            if (socketRef.current?.connected) {
              socketRef.current.emit('terminal:resize', {
                terminalId,
                cols: data.cols,
                rows: data.rows
              });
            }

            sendMessageToParent('TERMINAL_RESIZED', {
              cols: data.cols,
              rows: data.rows,
              success: true
            });
          } catch (error) {
            sendMessageToParent('TERMINAL_RESIZED', {
              success: false,
              error: error.message
            });
          }
        }
        break;

      case 'TERMINAL_CLEAR':
        // Appthen 请求清空终端
        if (terminalInstance.current) {
          terminalInstance.current.clear();
          sendMessageToParent('TERMINAL_CLEARED', { success: true });
        }
        break;

      default:
        console.log('🔌 Terminal unknown message type:', type);
    }
  }, [terminalId, isConnected, error, sendMessageToParent]);

  // 设置 PostMessage 监听器
  useEffect(() => {
    window.addEventListener('message', handleParentMessage);

    // 终端加载完成后通知父窗口
    sendMessageToParent('TERMINAL_LOADED', {
      terminalId,
      ready: isConnected && !!terminalInstance.current
    });

    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, [handleParentMessage, sendMessageToParent, terminalId, isConnected]);

  // 当连接状态变化时通知父窗口
  useEffect(() => {
    sendMessageToParent('TERMINAL_STATUS_CHANGED', {
      terminalId,
      connected: isConnected,
      ready: isConnected && !!terminalInstance.current,
      error: error
    });
  }, [isConnected, error, sendMessageToParent, terminalId]);

  useEffect(() => {
    if (!terminalId) {
      setError('终端 ID 未提供');
      return;
    }

    // 检查并保存 URL 中的 token 到 localStorage
    const tokenFromQuery = searchParams.get('token');
    if (tokenFromQuery) {
      console.log('🔑 Saving token from URL to localStorage');
      localStorage.setItem('auth_token', tokenFromQuery);
    }

    // 检查是否有有效的 token
    const token = getAuthToken();
    if (!token) {
      setError('认证失败：未找到登录令牌。请确保在 URL 中传入 token 参数，例如：/plugin/terminal/xxx?token=your-token');
      return;
    }

    console.log('🔑 Token found, initializing terminal...');
    initializeTerminal();

    return () => {
      cleanup();
    };
  }, [terminalId, searchParams, getAuthToken]);

  const initializeTerminal = async () => {
    try {
      // 创建终端实例 - 强制暗黑主题
      const terminal = new Terminal({
        cursorBlink: true,
        fontSize: 14,
        fontFamily: 'Consolas, "Courier New", monospace',
        theme: {
          background: '#0d1117',      // GitHub 暗黑主题背景
          foreground: '#e6edf3',      // 主要文字颜色
          cursor: '#f0f6fc',          // 光标颜色
          cursorAccent: '#0d1117',    // 光标背景
          selection: '#264f78',       // 选择背景
          selectionForeground: '#ffffff', // 选择文字
          black: '#484f58',           // 黑色
          red: '#ff7b72',             // 红色
          green: '#3fb950',           // 绿色
          yellow: '#d29922',          // 黄色
          blue: '#58a6ff',            // 蓝色
          magenta: '#bc8cff',         // 洋红
          cyan: '#39c5cf',            // 青色
          white: '#b1bac4',           // 白色
          brightBlack: '#6e7681',     // 亮黑
          brightRed: '#ffa198',       // 亮红
          brightGreen: '#56d364',     // 亮绿
          brightYellow: '#e3b341',    // 亮黄
          brightBlue: '#79c0ff',      // 亮蓝
          brightMagenta: '#d2a8ff',   // 亮洋红
          brightCyan: '#56d4dd',      // 亮青
          brightWhite: '#f0f6fc'      // 亮白
        },
        allowTransparency: false,     // 禁用透明度，确保暗黑背景
        convertEol: true,
        scrollback: 1000,
        tabStopWidth: 4,
        windowsMode: false,           // 确保 Unix 模式
        macOptionIsMeta: true,        // Mac 选项键作为 Meta
        rightClickSelectsWord: true,  // 右键选择单词
        fastScrollModifier: 'shift'   // Shift 快速滚动
      });

      // 添加插件
      fitAddon.current = new FitAddon();
      terminal.loadAddon(fitAddon.current);
      
      try {
        const webglAddon = new WebglAddon();
        terminal.loadAddon(webglAddon);
      } catch (e) {
        console.warn('WebGL addon failed to load:', e);
      }

      try {
        const clipboardAddon = new ClipboardAddon();
        terminal.loadAddon(clipboardAddon);
      } catch (e) {
        console.warn('Clipboard addon failed to load:', e);
      }

      // 挂载到 DOM
      terminal.open(terminalRef.current);
      terminalInstance.current = terminal;

      // 适配大小
      fitAddon.current.fit();

      // 连接 Socket.IO
      await connectSocket();

      // 监听窗口大小变化
      const handleResize = () => {
        if (fitAddon.current && terminalInstance.current) {
          fitAddon.current.fit();
          // 通知后端终端大小变化
          if (socketRef.current) {
            socketRef.current.emit('terminal:resize', {
              terminalId,
              cols: terminalInstance.current.cols,
              rows: terminalInstance.current.rows
            });
          }
        }
      };

      window.addEventListener('resize', handleResize);
      
      // 初始化后立即调整大小
      setTimeout(handleResize, 100);

      return () => {
        window.removeEventListener('resize', handleResize);
      };

    } catch (error) {
      console.error('Failed to initialize terminal:', error);
      setError('终端初始化失败: ' + error.message);
    }
  };

  const connectSocket = () => {
    return new Promise((resolve, reject) => {
      try {
        const token = getAuthToken();
        if (!token) {
          console.error('No authentication token found');
          setError('认证失败：未找到登录令牌。请确保在 URL 中传入 token 参数，例如：/plugin/terminal/xxx?token=your-token');
          reject(new Error('No auth token'));
          return;
        }

        // 使用配置的后端 WebSocket URL
        const backendUrl = API_CONFIG.WS_BASE_URL;

        console.log('Connecting to Socket.IO:', `${backendUrl}/terminal`);

        // 创建 Socket.IO 连接到 /terminal 命名空间
        const socket = io(`${backendUrl}/terminal`, {
          auth: { token },
          transports: ['polling'], // 与SocketIOTerminal保持一致
          upgrade: false,
          rememberUpgrade: false,
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 10000,
          timeout: 30000,
          forceNew: false,
          multiplex: true
        });

        socketRef.current = socket;

        socket.on('connect', () => {
          console.log('Socket.IO connected:', socket.id);
          setIsConnected(true);
          setError(null);

          // 使用与SocketIOTerminal相同的连接事件
          const connectData = {
            terminalId: terminalId,
            action: 'connect',
            cols: terminalInstance.current?.cols || 80,
            rows: terminalInstance.current?.rows || 24,
            requestHistory: true,
            // 添加清理标志，避免重复的提示符
            cleanReconnect: true
          };

          console.log('Sending terminal:connect event:', connectData);
          socket.emit('terminal:connect', connectData);

          resolve();
        });

        // 设置终端事件监听器
        setupTerminalEventListeners(socket);

        socket.on('disconnect', (reason) => {
          console.log('Socket.IO disconnected:', reason);
          setIsConnected(false);
          if (reason === 'io server disconnect') {
            // 服务器主动断开，尝试重连
            socket.connect();
          }
        });

        socket.on('connect_error', (error) => {
          console.error('Socket.IO connection error:', error);
          setError('连接失败: ' + error.message);
          setIsConnected(false);
          reject(error);
        });

        socket.on('terminal:error', (data) => {
          if (data.terminalId === terminalId) {
            console.error('Terminal error:', data.error);
            setError('终端错误: ' + data.error);
          }
        });

        socket.on('terminal:exit', (data) => {
          if (data.terminalId === terminalId) {
            console.log('Terminal exited:', data);
            if (terminalInstance.current) {
              terminalInstance.current.write('\r\n\x1b[31m终端会话已结束\x1b[0m\r\n');
            }
          }
        });

      } catch (error) {
        console.error('Failed to create socket connection:', error);
        setError('连接创建失败: ' + error.message);
        reject(error);
      }
    });
  };

  const setupTerminalEventListeners = (socket) => {
    // 监听终端数据输出 - 支持多种事件名称
    socket.on('terminal:data', (data) => {
      if (terminalInstance.current) {
        terminalInstance.current.write(data);
      }
    });

    socket.on('terminal:terminal:data', (data) => {
      if (terminalInstance.current) {
        terminalInstance.current.write(data);
      }
    });

    // 监听终端连接成功
    socket.on('terminal:connected', (data) => {
      console.log('Terminal connected:', data);
      setError(null);
    });

    socket.on('terminal:created', (data) => {
      console.log('Terminal created:', data);
      setError(null);
    });

    // 设置终端输入处理
    if (terminalInstance.current) {
      terminalInstance.current.onData((data) => {
        if (socket && socket.connected) {
          socket.emit('terminal:input', {
            terminalId: terminalId,
            input: data
          });
        }
      });

      // 设置终端大小变化处理
      terminalInstance.current.onResize(({ cols, rows }) => {
        if (socket && socket.connected) {
          socket.emit('terminal:resize', {
            terminalId: terminalId,
            cols,
            rows
          });
        }
      });
    }
  };

  const cleanup = () => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    if (terminalInstance.current) {
      terminalInstance.current.dispose();
      terminalInstance.current = null;
    }

    setIsConnected(false);
  };

  // 处理键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+C 复制
      if (e.ctrlKey && e.key === 'c' && terminalInstance.current?.hasSelection()) {
        e.preventDefault();
        document.execCommand('copy');
      }
      
      // Ctrl+V 粘贴
      if (e.ctrlKey && e.key === 'v') {
        e.preventDefault();
        navigator.clipboard.readText().then(text => {
          if (terminalInstance.current && socketRef.current) {
            socketRef.current.emit('terminal:input', {
              terminalId,
              data: text
            });
          }
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [terminalId]);

  if (authLoading) {
    return (
      <div className="w-full h-screen bg-black flex items-center justify-center">
        <div className="text-white text-sm">加载中...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="w-full h-screen bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-lg font-medium mb-2">需要登录</div>
          <div className="text-sm text-gray-400 mb-4">请先在插件面板中登录</div>
          <button
            onClick={() => window.parent.postMessage({ type: 'SHOW_LOGIN' }, '*')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            返回登录
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-screen bg-red-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">终端加载失败</div>
          <div className="text-red-500 text-sm">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            重新加载
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="plugin-terminal">
      {/* 错误显示 */}
      {error && (
        <div className="plugin-error dark">
          <div className="error-content">
            <div className="error-title">终端连接失败</div>
            <div className="error-message">{error}</div>
            <button
              className="error-button"
              onClick={() => {
                setError(null);
                initializeTerminal();
              }}
            >
              重试连接
            </button>
          </div>
        </div>
      )}

      {/* 连接状态指示器 */}
      {!error && (
        <div className={`connection-indicator ${isConnected ? 'connected' : 'connecting'}`}>
          {isConnected ? '已连接' : '连接中...'}
        </div>
      )}

      {/* 终端容器 */}
      {!error && (
        <div
          ref={terminalRef}
          className="w-full h-full"
          style={{
            fontFamily: 'Consolas, "Courier New", monospace',
            fontSize: '14px',
            background: '#0d1117'  // 强制暗黑背景
          }}
        />
      )}

      {/* 键盘快捷键提示 */}
      {!error && isConnected && (
        <div className="keyboard-hint">
          Ctrl+C 复制 | Ctrl+V 粘贴 | Ctrl+Shift+C/V 终端复制粘贴
        </div>
      )}
    </div>
  );
};

export default PluginTerminalPage;
